import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Plus, X, Save, Trash2, Map, Grid3X3,
  Square, Settings2, Users, LayoutDashboard,
  AlignHorizontalJustifyCenter, ZoomIn, ZoomOut,
  RotateCcw, CheckCircle2, Columns, Loader2
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/floorplan.css';
import axios from 'axios';
import { baseurl } from '../../baseurl';

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const SHAPE_OPTIONS = [
  { key: 'square', label: 'Square',    icon: '■', w: 72,  h: 72  },
  { key: 'circle', label: 'Round',     icon: '●', w: 72,  h: 72  },
  { key: 'rect',   label: 'Rectangle', icon: '▬', w: 100, h: 60  },
  { key: 'booth',  label: 'Booth',     icon: '⌂', w: 90,  h: 70  },
];

const STATUS_OPTIONS = [
  { key: 'available', label: 'Free',     color: '#00c896' },
  { key: 'occupied',  label: 'Occupied', color: '#ff4d2e' },
  { key: 'reserved',  label: 'Reserved', color: '#ff9f1c' },
];

const OBSTACLE_TYPES = [
  { key: 'wall',   label: 'Wall',   w: 100, h: 18 },
  { key: 'pillar', label: 'Pillar', w: 30,  h: 30 },
  { key: 'bar',    label: 'Bar',    w: 140, h: 40 },
];

/* ═══════════════════════════════════════════════
   AXIOS INSTANCE  (reads token each call)
═══════════════════════════════════════════════ */
const getAxios = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({
    baseURL: `${baseurl}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
};

/* ═══════════════════════════════════════════════
   TOAST HOOK
═══════════════════════════════════════════════ */
function useToast() {
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
  const fire = useCallback((msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 2800);
  }, []);
  return { toast, fire };
}

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
function FloorPlanDesigner() {

  /* ── State ── */
  const [zones,         setZones]         = useState([]);
  const [activeZone,    setActiveZone]    = useState(null);   // { id, name }
  const [tables,        setTables]        = useState([]);
  const [obstacles,     setObstacles]     = useState([]);
  const [selectedId,    setSelectedId]    = useState(null);
  const [selectedType,  setSelectedType]  = useState(null);   // 'table' | 'obstacle'
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [newZoneName,   setNewZoneName]   = useState('');
  const [snapToGrid,    setSnapToGrid]    = useState(true);
  const [zoom,          setZoom]          = useState(1);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);

  const floorRef = useRef(null);
  const { toast, fire } = useToast();

  /* ═══════════════════════════════════════════════
     LOAD ALL DATA ON MOUNT
  ═══════════════════════════════════════════════ */
  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const api = getAxios();
      const [zonesRes, tablesRes, obstaclesRes] = await Promise.all([
        api.get('/zones'),
        api.get('/tables'),
        api.get('/obstacles'),
      ]);

      const fetchedZones = zonesRes.data.zones || [];
      setZones(fetchedZones);
      if (fetchedZones.length > 0) setActiveZone(fetchedZones[0]);

      setTables((tablesRes.data.tables || []).map(normaliseTable));
      setObstacles((obstaclesRes.data.obstacles || []).map(normaliseObstacle));
    } catch (err) {
      fire('Failed to load floor plan data', 'error');
      console.error('loadAll Error:', err);
    } finally {
      setLoading(false);
    }
  };

  /* DB (pos_x/pos_y) → canvas (x/y) mapping */
  const normaliseTable = (t) => ({
    ...t,
    x:    t.pos_x    ?? t.x    ?? 50,
    y:    t.pos_y    ?? t.y    ?? 50,
    zone: t.zone_id  ?? t.zone,
  });

  const normaliseObstacle = (o) => ({
    ...o,
    x:    o.pos_x   ?? o.x    ?? 50,
    y:    o.pos_y   ?? o.y    ?? 50,
    w:    o.width   ?? o.w    ?? 80,
    h:    o.height  ?? o.h    ?? 20,
    zone: o.zone_id ?? o.zone,
  });

  /* ═══════════════════════════════════════════════
     DERIVED DATA
  ═══════════════════════════════════════════════ */
  const zoneTables     = useMemo(() => tables.filter(t    => t.zone    === activeZone?.id), [tables,    activeZone]);
  const zoneObstacles  = useMemo(() => obstacles.filter(o => o.zone    === activeZone?.id), [obstacles, activeZone]);
  const selectedTable  = useMemo(() => selectedType === 'table'    ? tables.find(t    => t.id === selectedId) : null, [selectedId, selectedType, tables]);
  const selectedObstacle = useMemo(() => selectedType === 'obstacle' ? obstacles.find(o => o.id === selectedId) : null, [selectedId, selectedType, obstacles]);
  const zoneTableCount = useCallback((zoneId) => tables.filter(t => t.zone === zoneId).length, [tables]);

  /* ═══════════════════════════════════════════════
     ZONE ACTIONS
  ═══════════════════════════════════════════════ */

  // POST /floor/zones
  const handleAddZone = async () => {
    if (!newZoneName.trim()) return;
    if (zones.some(z => z.name === newZoneName.trim())) {
      fire('A zone with this name already exists', 'error');
      return;
    }
    try {
      const res     = await getAxios().post('/zones', { name: newZoneName.trim() });
      const newZone = res.data.zone;
      setZones(prev => [...prev, newZone]);
      setActiveZone(newZone);
      setNewZoneName('');
      setShowZoneModal(false);
      fire(`Zone "${newZone.name}" created`);
    } catch (err) {
      fire(err.response?.data?.message || 'Failed to create zone', 'error');
    }
  };

  // DELETE /floor/zones/:zoneId
  const handleDeleteZone = async (zone) => {
    if (zones.length <= 1) { fire('Cannot delete the last zone', 'error'); return; }
    try {
      await getAxios().delete(`/zones/${zone.id}`);
      setTables(prev    => prev.filter(t    => t.zone !== zone.id));
      setObstacles(prev => prev.filter(o    => o.zone !== zone.id));
      setZones(prev     => prev.filter(z    => z.id   !== zone.id));
      setActiveZone(prev =>
        prev?.id === zone.id ? (zones.find(z => z.id !== zone.id) ?? null) : prev
      );
      setSelectedId(null);
      fire(`Zone "${zone.name}" deleted`);
    } catch (err) {
      fire(err.response?.data?.message || 'Failed to delete zone', 'error');
    }
  };

  /* ═══════════════════════════════════════════════
     TABLE ACTIONS
  ═══════════════════════════════════════════════ */

  // POST /floor/tables
  const handleAddTable = async (shape = 'square') => {
    if (!activeZone) return;
    const s     = SHAPE_OPTIONS.find(s => s.key === shape);
    const snapV = (v) => snapToGrid ? Math.round(v / 20) * 20 : v;
    const payload = {
      zone_id:  activeZone.id,
      label:    `T${tables.length + 1}`,
      shape,
      capacity: 4,
      status:   'available',
      pos_x:    snapV(40 + Math.floor(Math.random() * 120)),
      pos_y:    snapV(40 + Math.floor(Math.random() * 120)),
      width:    s?.w ?? 72,
      height:   s?.h ?? 72,
    };
    try {
      const res  = await getAxios().post('/tables', payload);
      const newT = normaliseTable(res.data.table);
      setTables(prev => [...prev, newT]);
      setSelectedId(newT.id);
      setSelectedType('table');
      fire(`Table ${newT.label} added`);
    } catch (err) {
      fire(err.response?.data?.message || 'Failed to add table', 'error');
    }
  };

  // PUT /floor/tables/:tableId  — optimistic update
  const handleUpdateTable = async (updates) => {
    setTables(prev => prev.map(t => t.id === selectedId ? { ...t, ...updates } : t));
    try {
      const dbUpdates = { ...updates };
      if ('x' in dbUpdates) { dbUpdates.pos_x = dbUpdates.x; delete dbUpdates.x; }
      if ('y' in dbUpdates) { dbUpdates.pos_y = dbUpdates.y; delete dbUpdates.y; }
      await getAxios().put(`/tables/${selectedId}`, dbUpdates);
    } catch (err) {
      fire('Failed to update table', 'error');
    }
  };

  // PUT /floor/tables/:tableId/status
  const handleTableStatusChange = async (tableId, status) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, status } : t));
    try {
      await getAxios().put(`/tables/${tableId}/status`, { status });
    } catch (err) {
      fire('Failed to update status', 'error');
    }
  };

  // DELETE /floor/tables/:tableId
  const handleDeleteTable = async () => {
    try {
      await getAxios().delete(`/tables/${selectedId}`);
      setTables(prev => prev.filter(t => t.id !== selectedId));
      setSelectedId(null);
      setSelectedType(null);
      fire('Table removed');
    } catch (err) {
      fire('Failed to remove table', 'error');
    }
  };

  /* ═══════════════════════════════════════════════
     OBSTACLE ACTIONS
  ═══════════════════════════════════════════════ */

  // POST /floor/obstacles
  const handleAddObstacle = async (type) => {
    if (!activeZone) return;
    const def = OBSTACLE_TYPES.find(o => o.key === type);
    try {
      const res  = await getAxios().post('/obstacles', {
        zone_id: activeZone.id, label: def.label, type,
        pos_x: 60, pos_y: 60, width: def.w, height: def.h,
      });
      const newO = normaliseObstacle(res.data.obstacle);
      setObstacles(prev => [...prev, newO]);
      setSelectedId(newO.id);
      setSelectedType('obstacle');
      fire(`${def.label} added`);
    } catch (err) {
      fire('Failed to add obstacle', 'error');
    }
  };

  // PUT /floor/obstacles/:obstacleId
  const handleUpdateObstacle = async (updates) => {
    setObstacles(prev => prev.map(o => o.id === selectedId ? { ...o, ...updates } : o));
    try {
      const dbUpdates = { ...updates };
      if ('w' in dbUpdates) { dbUpdates.width  = dbUpdates.w; delete dbUpdates.w; }
      if ('h' in dbUpdates) { dbUpdates.height = dbUpdates.h; delete dbUpdates.h; }
      await getAxios().put(`/obstacles/${selectedId}`, dbUpdates);
    } catch (err) {
      fire('Failed to update obstacle', 'error');
    }
  };

  // DELETE /floor/obstacles/:obstacleId
  const handleDeleteObstacle = async () => {
    try {
      await getAxios().delete(`/obstacles/${selectedId}`);
      setObstacles(prev => prev.filter(o => o.id !== selectedId));
      setSelectedId(null);
      setSelectedType(null);
      fire('Object removed');
    } catch (err) {
      fire('Failed to remove object', 'error');
    }
  };

  const handleDeleteSelected = () => {
    if (selectedType === 'table')    handleDeleteTable();
    else if (selectedType === 'obstacle') handleDeleteObstacle();
  };

  /* ═══════════════════════════════════════════════
     DRAG → PUT /floor/tables/bulk-positions
             PUT /floor/obstacles/:id
  ═══════════════════════════════════════════════ */
  const snapV = useCallback(
    (v) => snapToGrid ? Math.round(v / 20) * 20 : v,
    [snapToGrid]
  );

  const handleTableDragEnd = async (e, id) => {
    const rect = floorRef.current.getBoundingClientRect();
    const x = Math.max(0, snapV(e.clientX - rect.left - 36));
    const y = Math.max(0, snapV(e.clientY - rect.top  - 36));
    setTables(prev => prev.map(t => t.id === id ? { ...t, x, y } : t));
    try {
      await getAxios().put('/tables/bulk-positions', { tables: [{ id, pos_x: x, pos_y: y }] });
    } catch (err) {
      fire('Position save failed', 'error');
    }
  };

  const handleObstacleDragEnd = async (e, id) => {
    const rect = floorRef.current.getBoundingClientRect();
    const x = Math.max(0, snapV(e.clientX - rect.left - 20));
    const y = Math.max(0, snapV(e.clientY - rect.top  - 10));
    setObstacles(prev => prev.map(o => o.id === id ? { ...o, x, y } : o));
    try {
      await getAxios().put(`/obstacles/${id}`, { pos_x: x, pos_y: y });
    } catch (err) {
      fire('Position save failed', 'error');
    }
  };

  /* ═══════════════════════════════════════════════
     SAVE ENTIRE LAYOUT → POST /floor/save-layout
  ═══════════════════════════════════════════════ */
  const handleSaveLayout = async () => {
    setSaving(true);
    try {
      await getAxios().post('/save-layout', {
        zones: zones.map(z => ({ id: z.id, name: z.name })),
        tables: tables.map(t => ({
          id: t.id, zone_id: t.zone, label: t.label,
          shape: t.shape, capacity: t.capacity,
          pos_x: t.x, pos_y: t.y,
          width: tableDims(t.shape).width, height: tableDims(t.shape).height,
          status: t.status,
        })),
        obstacles: obstacles.map(o => ({
          id: o.id, zone_id: o.zone, label: o.label, type: o.type,
          pos_x: o.x, pos_y: o.y, width: o.w, height: o.h,
        })),
      });
      fire('Floor plan saved successfully!');
    } catch (err) {
      fire(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ═══════════════════════════════════════════════
     CLEAR ZONE  — DELETE each table in the zone
  ═══════════════════════════════════════════════ */
  const handleClearZone = async () => {
    if (!activeZone) return;
    const zoneTbls = tables.filter(t => t.zone === activeZone.id);
    try {
      await Promise.all(zoneTbls.map(t => getAxios().delete(`/tables/${t.id}`)));
      setTables(prev => prev.filter(t => t.zone !== activeZone.id));
      fire('Zone cleared');
    } catch (err) {
      fire('Failed to clear zone', 'error');
    }
  };

  /* ═══════════════════════════════════════════════
     ZOOM & MISC
  ═══════════════════════════════════════════════ */
  const handleZoom      = (dir) => setZoom(prev => Math.min(2, Math.max(0.5, +(prev + dir * 0.1).toFixed(1))));
  const resetZoom       = ()    => setZoom(1);
  const handleCanvasClick = (e) => {
    if (e.target === floorRef.current || e.target.classList.contains('fp-canvas-inner')) {
      setSelectedId(null);
      setSelectedType(null);
    }
  };
  const tableDims = (shape) => {
    const s = SHAPE_OPTIONS.find(s => s.key === shape);
    return s ? { width: s.w, height: s.h } : { width: 72, height: 72 };
  };

  /* ═══════════════════════════════════════════════
     LOADING SCREEN
  ═══════════════════════════════════════════════ */
  if (loading) {
    return (
      <div className="fp-root">
        <Navbar />
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 16, color: 'var(--muted)',
        }}>
          <Loader2 size={36} style={{ animation: 'spin 1s linear infinite', color: 'var(--tomato)' }} />
          <p style={{ fontWeight: 600 }}>Loading floor plan…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════ */
  return (
    <div className="fp-root">
      <Navbar />

      {/* ══ TOOLBAR ══ */}
      <div className="fp-toolbar">
        <div className="fp-toolbar-left">
          <h2 className="fp-page-title">Floor <span>Plan</span></h2>
          {activeZone && (
            <span className="fp-zone-badge">
              <Map size={12} /> {activeZone.name}
            </span>
          )}
        </div>

        <div className="fp-toolbar-center">
          {SHAPE_OPTIONS.map(s => (
            <button
              key={s.key}
              className={`fp-add-btn ${s.key === 'square' || s.key === 'circle' ? 'primary' : ''}`}
              onClick={() => handleAddTable(s.key)}
              title={`Add ${s.label} Table`}
            >
              <span style={{ fontSize: 15 }}>{s.icon}</span>
              {s.label}
            </button>
          ))}

          <div className="fp-sep" />

          {OBSTACLE_TYPES.map(o => (
            <button
              key={o.key}
              className="fp-add-btn"
              onClick={() => handleAddObstacle(o.key)}
              title={`Add ${o.label}`}
            >
              {o.key === 'wall'   && <Columns size={14} />}
              {o.key === 'pillar' && <Square  size={14} />}
              {o.key === 'bar'    && <AlignHorizontalJustifyCenter size={14} />}
              {o.label}
            </button>
          ))}
        </div>

        <div className="fp-toolbar-right">
          <button
            className={`fp-snap-btn ${snapToGrid ? 'active' : ''}`}
            onClick={() => setSnapToGrid(s => !s)}
          >
            <Grid3X3 size={14} /> Grid Snap
          </button>
          <button className="fp-clear-btn" onClick={handleClearZone}>
            <RotateCcw size={14} /> Clear
          </button>
          <button className="fp-save-btn" onClick={handleSaveLayout} disabled={saving}>
            {saving
              ? <><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Saving…</>
              : <><Save size={15} /> Save Layout</>
            }
          </button>
        </div>
      </div>

      {/* ══ MAIN ══ */}
      <div className="fp-main">

        {/* ── Left Sidebar: Zones ── */}
        <aside className="fp-zone-sidebar">
          <div className="fp-sidebar-header">
            <div className="fp-sidebar-title">
              <Map size={13} /> Zones
            </div>
          </div>

          <div className="fp-zone-list">
            {zones.map(zone => (
              <div
                key={zone.id}
                className={`fp-zone-row ${activeZone?.id === zone.id ? 'active' : ''}`}
              >
                <button
                  className="fp-zone-btn"
                  onClick={() => { setActiveZone(zone); setSelectedId(null); setSelectedType(null); }}
                >
                  <span className="fp-zone-icon"><Map size={13} /></span>
                  <span style={{ flex: 1, textAlign: 'left', fontSize: 13 }}>{zone.name}</span>
                  <span className="fp-zone-count">{zoneTableCount(zone.id)}</span>
                </button>
                <button
                  className="fp-zone-del"
                  onClick={() => handleDeleteZone(zone)}
                  title="Delete zone"
                >
                  <X size={12} />
                </button>
              </div>
            ))}

            <button className="fp-zone-add" onClick={() => setShowZoneModal(true)}>
              <Plus size={14} /> Add Zone
            </button>
          </div>

          {/* Stats */}
          <div className="fp-sidebar-stats">
            <div className="fp-stat-row">
              <span className="fp-stat-label">Total Tables</span>
              <span className="fp-stat-val">{tables.length}</span>
            </div>
            <div className="fp-stat-row">
              <span className="fp-stat-label">Available</span>
              <span className="fp-stat-val" style={{ color: 'var(--mint-d)' }}>
                {tables.filter(t => t.status === 'available').length}
              </span>
            </div>
            <div className="fp-stat-row">
              <span className="fp-stat-label">Occupied</span>
              <span className="fp-stat-val" style={{ color: 'var(--tomato)' }}>
                {tables.filter(t => t.status === 'occupied').length}
              </span>
            </div>
            <div className="fp-stat-row">
              <span className="fp-stat-label">Zone Capacity</span>
              <span className="fp-stat-val">
                {zoneTables.reduce((a, t) => a + (t.capacity || 0), 0)} seats
              </span>
            </div>
          </div>
        </aside>

        {/* ── Canvas ── */}
        <section
          className="fp-canvas-wrap"
          ref={floorRef}
          onClick={handleCanvasClick}
        >
          <div
            className="fp-canvas-inner"
            style={{
              transform: `scale(${zoom})`, transformOrigin: 'top left',
              width: `${100 / zoom}%`, height: `${100 / zoom}%`,
              position: 'absolute', inset: 0,
            }}
          >
            {/* Obstacles */}
            {zoneObstacles.map(obs => (
              <div
                key={obs.id}
                className={`fp-obstacle ${selectedId === obs.id ? 'fp-selected' : ''}`}
                style={{ left: obs.x, top: obs.y, width: obs.w, height: obs.h }}
                onClick={(e) => { e.stopPropagation(); setSelectedId(obs.id); setSelectedType('obstacle'); }}
                draggable
                onDragEnd={(e) => handleObstacleDragEnd(e, obs.id)}
              >
                {obs.label}
              </div>
            ))}

            {/* Tables */}
            {zoneTables.map(table => {
              const dims = tableDims(table.shape);
              return (
                <div
                  key={table.id}
                  className={`fp-table ${table.shape} ${selectedId === table.id ? 'fp-selected' : ''}`}
                  data-cap={table.capacity}
                  style={{ left: table.x, top: table.y, width: dims.width, height: dims.height }}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(table.id); setSelectedType('table'); }}
                  draggable
                  onDragEnd={(e) => handleTableDragEnd(e, table.id)}
                >
                  <div className="fp-table-body">
                    <span className={`fp-table-status ${table.status}`} />
                    <span className="fp-table-id">{table.label}</span>
                    <span className="fp-table-cap"><Users size={9} /> {table.capacity}</span>
                  </div>
                </div>
              );
            })}

            {/* Empty state */}
            {zoneTables.length === 0 && zoneObstacles.length === 0 && (
              <div className="fp-canvas-empty">
                <div className="fp-canvas-empty-icon"><LayoutDashboard size={28} /></div>
                <h4>Empty Zone</h4>
                <p>Add tables from the toolbar to start designing</p>
              </div>
            )}
          </div>

          <div className="fp-canvas-watermark">{activeZone?.name}</div>

          <div className="fp-legend">
            {STATUS_OPTIONS.map(s => (
              <div key={s.key} className="fp-legend-item">
                <span className="fp-legend-dot" style={{ background: s.color }} />
                {s.label}
              </div>
            ))}
          </div>

          <div className="fp-zoom-controls">
            <button className="fp-zoom-btn" onClick={() => handleZoom(1)}><ZoomIn size={16} /></button>
            <div className="fp-zoom-sep" />
            <button className="fp-zoom-btn" onClick={resetZoom}
              style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>
              {Math.round(zoom * 100)}%
            </button>
            <div className="fp-zoom-sep" />
            <button className="fp-zoom-btn" onClick={() => handleZoom(-1)}><ZoomOut size={16} /></button>
          </div>
        </section>

        {/* ── Right Panel: Properties ── */}
        <aside className={`fp-prop-panel ${!selectedId ? 'closed' : ''}`}>
          {selectedTable ? (
            <>
              <div className="fp-prop-header">
                <span className="fp-prop-title">Table Properties</span>
                <button className="fp-prop-close"
                  onClick={() => { setSelectedId(null); setSelectedType(null); }}>
                  <X size={15} />
                </button>
              </div>
              <div className="fp-prop-body">
                <div className="fp-prop-preview">
                  <div
                    className={`fp-prop-preview-table ${selectedTable.shape}`}
                    style={{ borderColor: STATUS_OPTIONS.find(s => s.key === selectedTable.status)?.color }}
                  >
                    {selectedTable.label}
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)' }}>
                      {selectedTable.capacity} seats
                    </span>
                  </div>
                </div>

                <div className="fp-prop-group">
                  <span className="fp-prop-label">Table Label</span>
                  <input
                    className="fp-prop-input"
                    value={selectedTable.label}
                    onChange={e => handleUpdateTable({ label: e.target.value })}
                    placeholder="e.g. T1, VIP-1, Window"
                  />
                </div>

                <div className="fp-prop-group">
                  <span className="fp-prop-label">Seating Capacity</span>
                  <div className="fp-cap-grid">
                    {[2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                      <button key={n}
                        className={`fp-cap-opt ${selectedTable.capacity === n ? 'active' : ''}`}
                        onClick={() => handleUpdateTable({ capacity: n })}>
                        <Users size={9} /> {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="fp-prop-group">
                  <span className="fp-prop-label">Table Shape</span>
                  <div className="fp-shape-grid">
                    {SHAPE_OPTIONS.map(s => (
                      <button key={s.key}
                        className={`fp-shape-opt ${selectedTable.shape === s.key ? 'active' : ''}`}
                        onClick={() => handleUpdateTable({ shape: s.key })}>
                        <div className={`fp-shape-icon ${s.key}`}>{s.icon}</div>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="fp-prop-group">
                  <span className="fp-prop-label">Current Status</span>
                  <div className="fp-status-grid">
                    {STATUS_OPTIONS.map(s => (
                      <button key={s.key}
                        className={`fp-status-opt ${s.key} ${selectedTable.status === s.key ? 'active' : ''}`}
                        onClick={() => handleTableStatusChange(selectedTable.id, s.key)}>
                        <span className={`fp-status-dot ${s.key}`} />
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <button className="fp-delete-btn" onClick={handleDeleteSelected}>
                  <Trash2 size={14} /> Remove Table
                </button>
              </div>
            </>

          ) : selectedObstacle ? (
            <>
              <div className="fp-prop-header">
                <span className="fp-prop-title">Object Properties</span>
                <button className="fp-prop-close"
                  onClick={() => { setSelectedId(null); setSelectedType(null); }}>
                  <X size={15} />
                </button>
              </div>
              <div className="fp-prop-body">
                <div className="fp-prop-group">
                  <span className="fp-prop-label">Label</span>
                  <input className="fp-prop-input" value={selectedObstacle.label}
                    onChange={e => handleUpdateObstacle({ label: e.target.value })}
                    placeholder="Wall, Bar, Pillar…" />
                </div>
                <div className="fp-prop-group">
                  <span className="fp-prop-label">Width (px)</span>
                  <input className="fp-prop-input" type="number" value={selectedObstacle.w}
                    onChange={e => handleUpdateObstacle({ w: +e.target.value })} />
                </div>
                <div className="fp-prop-group">
                  <span className="fp-prop-label">Height (px)</span>
                  <input className="fp-prop-input" type="number" value={selectedObstacle.h}
                    onChange={e => handleUpdateObstacle({ h: +e.target.value })} />
                </div>
                <button className="fp-delete-btn" onClick={handleDeleteSelected}>
                  <Trash2 size={14} /> Remove Object
                </button>
              </div>
            </>

          ) : (
            <div className="fp-prop-empty">
              <div className="fp-prop-empty-icon"><Settings2 size={24} /></div>
              <strong>Nothing selected</strong>
              <p>Click a table or object on the canvas to edit its properties</p>
            </div>
          )}
        </aside>
      </div>

      {/* ══ ZONE MODAL ══ */}
      {showZoneModal && (
        <div className="fp-modal-overlay" onClick={() => setShowZoneModal(false)}>
          <div className="fp-modal" onClick={e => e.stopPropagation()}>
            <div className="fp-modal-title">Add New Zone</div>
            <p className="fp-modal-sub">Create a new floor section or area for your restaurant</p>
            <input
              className="fp-modal-input"
              placeholder="e.g. Rooftop, Private Dining, Garden…"
              value={newZoneName}
              onChange={e => setNewZoneName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddZone()}
              autoFocus
            />
            <div className="fp-modal-actions">
              <button className="fp-modal-cancel" onClick={() => setShowZoneModal(false)}>Cancel</button>
              <button className="fp-modal-create" onClick={handleAddZone}>Create Zone</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ TOAST ══ */}
      <div className={`fp-toast ${toast.show ? 'show' : ''}`}>
        <CheckCircle2 size={15} className="fp-toast-icon" />
        {toast.msg}
      </div>
    </div>
  );
}

export default FloorPlanDesigner;
